import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { isNullOrUndefined } from '@sla-ng/core';
import { Moment } from 'moment';
import { BehaviorSubject, distinctUntilChanged, filter, firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { DateRange } from '@angular/material/datepicker';
import { GeneratePlanningDialogComponent } from '../../../components/generate-planning/generate-planning-dialog.component';
import { PlanningGenerationType } from '../../../models/enum/planning-generation-type.enum';
import { IPlanningGenerationSelection } from '../../../models/planning-generation-selection.model';
import { SharedDataFacade } from '../../../services/facades/master-data/shared-data.facade';

import { PlannedSaleVolumeDialogComponent } from './planned-sale-volume-dialog/planned-sale-volume-dialog.component';
import { ConfirmDialogComponent } from '@sla-ng/ui-material';
import { IPlannedSaleVolumeState } from '../../../services/facades/planning/planned-sale-volume-state.model';
import { IPlannedSaleVolumeLayout } from '../../../models/layout/planned-sale-volume-layout.model';
import { IPlannedSaleVolume } from '../../../models/planned-sale-volume.model';
import { PlannedSaleVolumeFacade } from '../../../services/facades/planning/planned-sale-volume.facade';
import { PlannedSaleVolumeLayoutService } from '../../../services/layout/planned-sale-volume-layout.service';

@Component({
    templateUrl: './planned-sale-volume-page.html',
    styleUrls: [ './planned-sale-volume-page.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlannedSaleVolumePage implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(MatSort)
    public sort: MatSort;

    @ViewChild(MatPaginator)
    public paginator: MatPaginator;

    public layout: IPlannedSaleVolumeLayout;
    public formGroup$: BehaviorSubject<FormGroup> = new BehaviorSubject<FormGroup>(null);

    public dataSource: MatTableDataSource<IPlannedSaleVolume> = new MatTableDataSource<IPlannedSaleVolume>([]);
    public state$: Observable<IPlannedSaleVolumeState>;
    private destroy$: Subject<void> = new Subject();

    constructor(private myPlannedSaleVolumeFacade: PlannedSaleVolumeFacade,
                private myMatDialog: MatDialog,
                private myPlannedPlannedSaleVolumeLayoutService: PlannedSaleVolumeLayoutService,
                private mySharedDataFacade: SharedDataFacade) {
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.myPlannedSaleVolumeFacade.clearState();
    }


    public ngOnInit(): void {
        this.layout = this.myPlannedPlannedSaleVolumeLayoutService.getPlannedSaleVolumeLayout();
        this.state$ = this.myPlannedSaleVolumeFacade.subState();
        this.state$
            .pipe(
                takeUntil(this.destroy$),
                map((s: IPlannedSaleVolumeState) => s.data),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((data: IPlannedSaleVolume[]) => {
                this.dataSource.data = data;
            });
        this.dataSource.sortingDataAccessor = (item: IPlannedSaleVolume, tableColumnName: string): string | number => {
            switch (tableColumnName) {
                case 'costUnit':
                    return item.costUnit.name;
                case 'articleNr':
                    return item.costUnit.articleNr;
                default:
                    return item[ tableColumnName ];
            }
        };

        void this.myPlannedSaleVolumeFacade.fetchPlannedSaleVolumes();

        void firstValueFrom(this.mySharedDataFacade.fetchArticles());
        void firstValueFrom(this.mySharedDataFacade.fetchMeasureUnits());
        void firstValueFrom(this.mySharedDataFacade.fetchCostUnits());
    }

    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    public openDialog(plannedPlannedSaleVolume?: IPlannedSaleVolume): void {
        const filterValues: { costUnitId: number, dateFilter: DateRange<Moment> } = this.formGroup$.value.getRawValue();
        const dialog: MatDialogRef<PlannedSaleVolumeDialogComponent> = this.myMatDialog.open(PlannedSaleVolumeDialogComponent, {
            width: '600px',
            data: isNullOrUndefined(plannedPlannedSaleVolume) ? { validFrom: filterValues.dateFilter.start, validTo: filterValues.dateFilter.end } : plannedPlannedSaleVolume,
        });
        dialog.afterClosed().subscribe(async () => {
            await this.fetchDataForFilter();
        });
    }

    public onDelete(price: IPlannedSaleVolume): void {
        const dialog: MatDialogRef<ConfirmDialogComponent> = this.myMatDialog.open(ConfirmDialogComponent, {
            width: '600px',
            data: {
                message: 'app.page.planned-sale-volume.confirm-delete.label',
            },
        });
        dialog.afterClosed().subscribe(async (isConfirmed: boolean) => {
            if (isConfirmed) {
                void await this.myPlannedSaleVolumeFacade.deletePlannedSaleVolume(price);
                void await this.fetchDataForFilter();
            }
        });
    }


    public formgroupChange(formGroup: FormGroup): void {
        formGroup.valueChanges.pipe(
            map((formData: { costUnitId: number | string, dateFilter: DateRange<Moment | string> }) => {
                // Sanitize data: FormGroup may also return empty Strings; Replace them with undefined
                const start: Moment = typeof formData.dateFilter.start === 'string' ? undefined : formData.dateFilter.start;
                const end: Moment = typeof formData.dateFilter.end === 'string' ? undefined : formData.dateFilter.end;
                const costUnitId: number = typeof formData.costUnitId === 'string' ? undefined : formData.costUnitId;

                if (formData.dateFilter.start === '') {
                    formGroup.controls[ 'dateFilter' ].get('start').patchValue(undefined, { emitEvent: false });
                }
                if (formData.dateFilter.end === '') {
                    formGroup.controls[ 'dateFilter' ].get('end').patchValue(undefined, { emitEvent: false });
                }
                if (formData.costUnitId === '') {
                    formGroup.controls[ 'costUnitId' ].patchValue(undefined, { emitEvent: false });
                }
                return {
                    costUnitId,
                    dateFilter: new DateRange<Moment>(start, end),
                };
            }),
            filter((v: { costUnitId: number, dateFilter: DateRange<Moment> }) => this.isDatefilterValid(v.dateFilter.start, v.dateFilter.end)),
            distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((v: { costUnitId: number, dateFilter: DateRange<Moment> }) => {
                void this.myPlannedSaleVolumeFacade.fetchPlannedSaleVolumes(v.costUnitId, v.dateFilter.start, v.dateFilter.end);
            },
            );
        this.formGroup$.next(formGroup);
    }

    public openGenerateDialog(): void {
        const dialog: MatDialogRef<GeneratePlanningDialogComponent> = this.myMatDialog.open(GeneratePlanningDialogComponent, {
            data: PlanningGenerationType.SALES_VOLUME,
            width: '600px',
        });
        dialog.afterClosed().subscribe(async (generationSelection: IPlanningGenerationSelection) => {
            if (!isNullOrUndefined(generationSelection)) {
                await this.myPlannedSaleVolumeFacade.generatePlannedVolumes(generationSelection);
                await this.fetchDataForFilter();
            }
        });
    }

    private async fetchDataForFilter(): Promise<void> {
        const filterValues: { costUnitId: number, dateFilter: DateRange<Moment> } = this.formGroup$.value.getRawValue();
        await this.myPlannedSaleVolumeFacade.fetchPlannedSaleVolumes(filterValues.costUnitId, filterValues.dateFilter.start, filterValues.dateFilter.end);
    }

    private isDatefilterValid(start: Moment, end: Moment): boolean {
        if (!isNullOrUndefined(start) && !isNullOrUndefined(end) && start.isAfter(end)) {
            return false;
        }
        /* Returns true if either both are undefined or both are set */
        return !((isNullOrUndefined(start) && !isNullOrUndefined(end)) || (isNullOrUndefined(end) && !isNullOrUndefined(start)));
    }
}
